import React, { useState, useRef } from 'react';
import { collection, addDoc, getDocs, query, where, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { Eye, Code2, Copy, Download, Save, CheckCircle, Loader2, Edit } from 'lucide-react';
import HtmlToolbar from '../ui/HtmlToolbar';
import { db } from '../../firebase/firebase';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const GeneratedDescription = ({
    currentUser,
    triggerAuthModal,
    generatedDescription,
    setGeneratedDescription,
    formSnapshot,
    showConfirmationModal,
    selectedTitle
}) => {
    const [showPreview, setShowPreview] = useState(true);
    const [copySuccess, setCopySuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const generatedDescriptionTextareaRef = useRef(null);
    const [isEditing, setIsEditing] = useState(false);

    const copyToClipboard = () => {
        if (!generatedDescription) return;
        const analytics = getAnalytics();
        navigator.clipboard.writeText(generatedDescription).then(() => {
            setCopySuccess('HTML Description copied!');
            logEvent(analytics, 'copy_to_clipboard', { content_type: 'description' });
            setTimeout(() => setCopySuccess(''), 2000);
        }).catch(err => {
            console.error('Failed to copy description:', err);
        });
    };

    const handleDownloadHtml = () => {
        if (!generatedDescription) return;
        const finalTitle = selectedTitle || formSnapshot.productName || 'tpt_description';
        const filename = `${finalTitle.trim().replace(/\s+/g, '_')}.html`;
        const blob = new Blob([generatedDescription], { type: 'text/html;charset=utf-8' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        const analytics = getAnalytics();
        logEvent(analytics, 'download_html', { filename: filename, content_length: generatedDescription.length });
    };

    const handleSaveDescriptionSnapshot = async () => {
        if (!currentUser) {
            triggerAuthModal("Please log in or sign up to save description snapshots.");
            return;
        }
        if (!generatedDescription.trim()) {
            setError("No new AI-generated description found to save.");
            return;
        }

        const finalTitle = selectedTitle || formSnapshot.productName || "My Saved Description";
        let saveName = window.prompt("Enter a name for this saved description snapshot:", finalTitle);
        if (!saveName || !saveName.trim()) return;
        saveName = saveName.trim();

        setIsLoading(true);
        setError('');

        const savedDescriptionsRef = collection(db, "users", currentUser.uid, "savedDescriptions");

        const performSave = async (isOverwrite = false) => {
            const snapshotData = {
                ...formSnapshot,
                productName: finalTitle,
                generatedDescription: generatedDescription,
                saveName: saveName,
                savedAt: serverTimestamp(),
            };

            if (isOverwrite) {
                const qOverwriteCheck = query(savedDescriptionsRef, where("saveName", "==", saveName));
                const overwriteSnapshot = await getDocs(qOverwriteCheck);
                if (!overwriteSnapshot.empty) {
                    await deleteDoc(doc(db, "users", currentUser.uid, "savedDescriptions", overwriteSnapshot.docs[0].id));
                }
            }

            await addDoc(savedDescriptionsRef, snapshotData);
            const analytics = getAnalytics();
            logEvent(analytics, 'save_description_snapshot', { name_length: saveName.length, overwrite: isOverwrite });
            setCopySuccess(`Description snapshot "${saveName}" saved successfully!`);
            setTimeout(() => setCopySuccess(''), 3000);
        };

        const qNameCheck = query(savedDescriptionsRef, where("saveName", "==", saveName));
        const nameCheckSnapshot = await getDocs(qNameCheck);

        if (!nameCheckSnapshot.empty) {
            showConfirmationModal(`A snapshot named "${saveName}" already exists. Overwrite it?`, async () => {
                await performSave(true);
                setIsLoading(false);
            }, () => setIsLoading(false));
        } else {
            await performSave(false);
            setIsLoading(false);
        }
    };

    if (!generatedDescription && !isEditing) {
        return null;
    }

    return (
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-xl mt-8">
            <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-4 gap-3">
                <h2 className="text-xl sm:text-2xl font-semibold text-sky-700">Generated Product Description</h2>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => setIsEditing(!isEditing)}
                        className={`flex items-center text-sm font-medium py-2 px-3 sm:px-4 rounded-lg transition-colors duration-150 ${isEditing ? 'bg-sky-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}`}
                    >
                        <Edit className="h-4 w-4 mr-2" />
                        {isEditing ? 'Done Editing' : 'Edit Description'}
                    </button>
                    <button onClick={() => setShowPreview(!showPreview)} className="flex items-center text-sm bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium py-2 px-3 sm:px-4 rounded-lg">
                        {showPreview ? <Code2 className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                        {showPreview ? 'Raw HTML' : 'Preview'}
                    </button>
                    <button onClick={copyToClipboard} className="flex items-center text-sm bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium py-2 px-3 sm:px-4 rounded-lg">
                        <Copy className="h-4 w-4 mr-2" /> Copy HTML
                    </button>
                    <button onClick={handleDownloadHtml} className="flex items-center text-sm bg-sky-200 hover:bg-sky-300 text-sky-700 font-medium py-2 px-3 sm:px-4 rounded-lg">
                        <Download className="h-4 w-4 mr-2" /> Download .html
                    </button>
                </div>
            </div>
            {copySuccess && (<div className="mb-3 p-2 bg-green-100 border border-green-400 text-green-700 rounded-md text-sm flex items-center"> <CheckCircle className="h-4 w-4 mr-2" /> {copySuccess} </div>)}
            {error && (<div className="mb-3 p-2 bg-red-100 border border-red-400 text-red-700 rounded-md text-sm">{error}</div>)}

            <button
                onClick={handleSaveDescriptionSnapshot}
                disabled={isLoading}
                className="mt-4 mb-4 w-full sm:w-auto bg-teal-500 hover:bg-teal-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md flex items-center justify-center transition-colors"
            >
                {isLoading ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Save size={18} className="mr-2" />}
                Save This Description & Inputs
            </button>

            <div className="mt-4 relative">
                {isEditing ? (
                    // When editing, show the Rich Text Editor with its default standard toolbar
                    <ReactQuill
                        theme="snow"
                        value={generatedDescription}
                        onChange={setGeneratedDescription}
                        className="bg-white"
                    />
                ) : showPreview ? (
                    // When not editing, show the formatted preview
                    <div className="prose max-w-none bg-sky-50 p-4 rounded-md border border-sky-200 overflow-y-auto max-h-[600px]" dangerouslySetInnerHTML={{ __html: generatedDescription }} />
                ) : (
                    // When not editing and not showing preview, show the raw HTML textarea
                    <>
                        <HtmlToolbar textareaRef={generatedDescriptionTextareaRef} onValueChange={setGeneratedDescription} />
                        <textarea
                            ref={generatedDescriptionTextareaRef}
                            value={generatedDescription}
                            onChange={(e) => setGeneratedDescription(e.target.value)}
                            className="w-full p-3 border border-slate-300 rounded-lg shadow-sm bg-sky-50 font-mono text-xs h-96"
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default GeneratedDescription;